import { Container, Graphics, Sprite, TilingSprite, type Texture } from 'pixi.js';
import { bakeFrames } from '../art/bake';
import { exhaustedVariant } from '../art/palettes';
import { GALLOP_FRAMES_PER_M, HORSE_GALLOP, HORSE_TUCK } from '../art/sprites/horse';
import {
  CLOUD_BIG,
  CLOUD_SMALL,
  DIRT_TILE,
  GRASS_TILE,
  HURDLE,
  TREE_BIG,
  TREE_SMALL,
} from '../art/sprites/scenery';
import { bindKeys } from '../input/InputController';
import { DustField, StrideEmitter } from '../render/effects';
import { VIRTUAL_H, VIRTUAL_W } from '../render/StageScaler';
import { xpFor } from '../sim/minigames/framework';
import { DRILL_TUNING, GallopSession } from '../sim/minigames/gallop';
import { el } from '../ui/dom';
import {
  InputCollector,
  SessionRunner,
  launchPalette,
  showTrainingResults,
  trainingControls,
  type TrainingLaunch,
  type TrainingSceneOptions,
} from './minigameCommon';
import type { Scene, SceneContext } from './SceneManager';

const PX_PER_M = 16;
const GROUND_Y = 208;
const HORSE_SCREEN_X = 120;
const JUMP_HEIGHT_PX = 32;


/** Speed training: the side-scrolling gallop drill. ↑ jumps. */
export class GallopDrillScene implements Scene {
  private readonly launch: TrainingLaunch;
  private readonly options: TrainingSceneOptions;
  private ctx!: SceneContext;
  private session!: GallopSession;
  private runner!: SessionRunner;
  private collector!: InputCollector;
  private container!: Container;
  private world!: Container;
  private meadow!: TilingSprite;
  private dirt!: TilingSprite;
  private horse!: Sprite;
  private gallopFrames!: Texture[];
  private blownFrames!: Texture[];
  private tuckTexture!: Texture;
  private hurdleSprites: Sprite[] = [];
  private dust!: DustField;
  private readonly emitter = new StrideEmitter();
  private lastDrawMs = 0;
  private hud!: HTMLElement;
  private timeText!: HTMLElement;
  private distText!: HTMLElement;
  private comboText!: HTMLElement;
  private heartsText!: HTMLElement;
  private flash!: HTMLElement;
  private unbind: (() => void) | null = null;
  private resultsShown = false;
  private disposeResults: (() => void) | null = null;
  private lastTextKey = '';

  constructor(launch: TrainingLaunch, options: TrainingSceneOptions) {
    this.launch = launch;
    this.options = options;
  }

  enter(ctx: SceneContext): void {
    this.ctx = ctx;
    this.session = new GallopSession(this.launch.tier, this.launch.seed);
    this.runner = new SessionRunner();
    this.collector = new InputCollector({ pressCodes: ['ArrowUp', 'KeyW'] });

    this.container = new Container();
    ctx.scaler.root.addChild(this.container);

    const sky = new Graphics()
      .rect(0, 0, VIRTUAL_W, 34)
      .fill(0x8fb0d0)
      .rect(0, 34, VIRTUAL_W, 46)
      .fill(0x87a8c8)
      .rect(0, 80, VIRTUAL_W, 10)
      .fill(0x6f94b8);
    this.container.addChild(sky);
    const grassTex = bakeFrames(GRASS_TILE.def, GRASS_TILE.palette)[0]!;
    this.meadow = new TilingSprite({ texture: grassTex, width: VIRTUAL_W, height: GROUND_Y - 6 - 90 });
    this.meadow.position.set(0, 90);
    this.container.addChild(this.meadow);
    for (const [piece, cx, cy] of [
      [CLOUD_BIG, 90, 12],
      [CLOUD_SMALL, 330, 26],
    ] as const) {
      const cloud = new Sprite(bakeFrames(piece.def, piece.palette)[0]!);
      cloud.position.set(cx, cy);
      this.container.addChild(cloud);
    }
    this.dirt = new TilingSprite({
      texture: bakeFrames(DIRT_TILE.def, DIRT_TILE.palette)[0]!,
      width: VIRTUAL_W,
      height: VIRTUAL_H - (GROUND_Y - 6),
    });
    this.dirt.position.set(0, GROUND_Y - 6);
    this.container.addChild(this.dirt);

    this.world = new Container();
    this.container.addChild(this.world);

    // No trackside rail here: at drill speed its posts strobed against
    // the hurdles and made the whole scene hard to read.
    const courseW = DRILL_TUNING.courseLengthM * PX_PER_M + VIRTUAL_W;
    const treeBigTex = bakeFrames(TREE_BIG.def, TREE_BIG.palette)[0]!;
    const treeSmallTex = bakeFrames(TREE_SMALL.def, TREE_SMALL.palette)[0]!;
    for (let x = 260; x < courseW; x += 340) {
      const big = x % 680 < 340;
      const tree = new Sprite(big ? treeBigTex : treeSmallTex);
      const def = big ? TREE_BIG.def : TREE_SMALL.def;
      tree.position.set(x, GROUND_Y - 8 - def.h);
      this.world.addChild(tree);
    }

    const hurdleTex = bakeFrames(HURDLE.def, HURDLE.palette)[0]!;
    for (const hurdle of this.session.drill.state.hurdles) {
      const sprite = new Sprite(hurdleTex);
      sprite.position.set(
        Math.round(hurdle.xM * PX_PER_M) - HURDLE.def.anchor[0],
        GROUND_Y + 4 - HURDLE.def.h,
      );
      this.world.addChild(sprite);
      this.hurdleSprites.push(sprite);
    }

    const coat = launchPalette(this.launch);
    this.dust = new DustField(this.world, 40);
    this.gallopFrames = bakeFrames(HORSE_GALLOP, coat);
    this.blownFrames = bakeFrames(HORSE_GALLOP, exhaustedVariant(coat));
    this.tuckTexture = bakeFrames(HORSE_TUCK, coat)[0]!;
    this.horse = new Sprite(this.gallopFrames[0]!);
    this.world.addChild(this.horse);

    this.timeText = el('span', { class: 'hud-chip' });
    this.distText = el('span', { class: 'hud-chip' });
    this.comboText = el('span', { class: 'hud-chip' });
    this.heartsText = el('span', { class: 'hud-chip bad' });
    this.flash = el('div', { class: 'race-flash' });
    this.hud = el(
      'div',
      { class: 'drill-hud with-training-controls' },
      el('div', { class: 'ranch-topbar' }, this.timeText, this.distText, this.comboText, this.heartsText),
      this.flash,
      trainingControls(this.collector, [
        { action: 'primary', mode: 'press', label: 'JUMP ↑', ariaLabel: 'Jump' },
      ]),
      el('div', { class: 'ranch-hints' }, '↑ / W or JUMP clears hurdles · presses buffer near landing · Esc ends early'),
    );
    ctx.uiRoot.append(this.hud);

    this.unbind = bindKeys([{ code: 'Escape', handler: () => this.endEarly() }]);
  }

  frame(nowMs: number): void {
    const steps = this.runner.advance(nowMs, this.session, this.collector);
    for (const event of this.session.drill.drainEvents()) {
      if (event.kind === 'hit') this.showFlash('STUMBLE!', 'bad');
      if (event.kind === 'burst') this.showFlash('COMBO BURST!', 'good');
    }
    this.draw();
    this.ctx.fps?.frame(nowMs, steps, 0);
    if (this.session.finished && !this.resultsShown) this.showResults();
  }

  private draw(): void {
    const state = this.session.drill.state;
    const camX = state.x * PX_PER_M - HORSE_SCREEN_X;
    this.world.position.x = -Math.round(camX);
    // The ground moves with the world (it's the plane she runs on); the
    // meadow behind drifts slower as parallax — one coherent motion field.
    this.dirt.tilePosition.x = -Math.round(camX);
    this.meadow.tilePosition.x = -Math.round(camX * 0.4);

    const totalAirTicks = Math.round(DRILL_TUNING.jumpAirS * 30);
    const airP = state.airborneTicks > 0 ? 1 - state.airborneTicks / totalAirTicks : 0;
    const jumpY = airP > 0 ? -Math.round(4 * JUMP_HEIGHT_PX * airP * (1 - airP)) : 0;

    // Hoof dust, thrown backwards on every stride.
    const nowMs = performance.now();
    const dtS = this.lastDrawMs === 0 ? 0 : Math.min(0.05, (nowMs - this.lastDrawMs) / 1000);
    this.lastDrawMs = nowMs;
    this.dust.update(dtS);
    const centerX = state.x * PX_PER_M;
    if (state.airborneTicks === 0 && state.v > 1 && this.emitter.step(state.x, 2.4)) {
      this.dust.emit(centerX - 14, GROUND_Y, Math.min(1.4, state.v / 10));
    }

    if (state.airborneTicks > 0) {
      this.horse.texture = this.tuckTexture;
    } else {
      const frames = state.stumbleTicks > 0 ? this.blownFrames : this.gallopFrames;
      this.horse.texture = frames[Math.floor(state.x * GALLOP_FRAMES_PER_M) % frames.length]!;
    }
    this.horse.tint = state.burstTicks > 0 ? 0xffe2b0 : 0xffffff;
    this.horse.position.set(
      Math.round(state.x * PX_PER_M) - HORSE_GALLOP.anchor[0],
      GROUND_Y - HORSE_GALLOP.h + jumpY,
    );

    state.hurdles.forEach((hurdle, index) => {
      const sprite = this.hurdleSprites[index];
      if (!sprite) return;
      if (hurdle.status === 'hit') sprite.tint = 0xd98a7c;
      else if (hurdle.status === 'cleared') sprite.tint = 0xa8e2a0;
    });

    const timeLeft = Math.max(0, DRILL_TUNING.durationS - state.t);
    const key = `${timeLeft.toFixed(1)}|${Math.floor(state.x)}|${state.combo}|${state.hits}`;
    if (key !== this.lastTextKey) {
      this.lastTextKey = key;
      this.timeText.textContent = `⏱ ${timeLeft.toFixed(1)}s`;
      this.distText.textContent = `${Math.floor(state.x)} m`;
      this.comboText.textContent = state.combo > 1 ? `combo ×${state.combo}` : '';
      this.heartsText.textContent = '♥'.repeat(Math.max(0, DRILL_TUNING.maxHits - state.hits));
    }
  }

  private endEarly(): void {
    if (this.session.finished) return;
    this.runner.fastForward(this.session);
  }

  private showFlash(text: string, tone: 'go' | 'good' | 'bad'): void {
    this.flash.textContent = text;
    this.flash.className = 'race-flash';
    void this.flash.offsetWidth;
    this.flash.className = `race-flash show ${tone}`;
  }

  private showResults(): void {
    this.resultsShown = true;
    const score = this.session.score();
    const xp = xpFor(score.raw, this.launch.xpMult);
    const finish = (): void => {
      const tape = [...this.runner.tape];
      this.ctx.scenes.pop();
      this.options.onDone(tape);
    };
    this.disposeResults = showTrainingResults(
      this.ctx.uiRoot,
      { title: 'Drill complete', detail: score.detail, xp, failed: score.failed },
      finish,
    );
    this.unbind?.();
    this.unbind = bindKeys([
      { code: 'Enter', inModal: true, handler: finish },
      { code: 'KeyT', inModal: true, handler: finish },
      { code: 'Escape', inModal: true, handler: finish },
    ]);
  }

  exit(): void {
    this.unbind?.();
    this.collector.dispose();
    this.disposeResults?.();
    this.hud.remove();
    this.container.destroy({ children: true });
  }
}
